import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { EventGQL, fetchSingleEventInfo } from '../../Api'
import { checkLiveGameRunning, isLiveGame, normalizeError, logEvent, executeWithRetry } from '../../Utils'
import {
  GAME_CHECK_INTERVAL_TIME,
  GAME_POLLING_INTERVAL_TIME,
} from '../../Constant'
import { GameDataHookReturn } from './types'
import { useLeagueContext } from '../../Context'

const DEDUPE_CONSTANT = 5000 // 5 seconds

export const useLiveGameData = (): GameDataHookReturn => {
  const { events, eventsLoading, updateEventInState } = useLeagueContext()
  const [liveGameEvent, setLiveGameEvent] = useState<EventGQL | null>(null)
  const [isEventLoading, setIsEventLoading] = useState<boolean>(false)

  const gameCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const liveGameIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef<boolean>(true)
  const currentGameIdRef = useRef<string | null>(null)
  const lastFetchTimeRef = useRef<number>(0)
  const inFlightRequestRef = useRef<Promise<EventGQL | null> | null>(null)

  const clearGameCheckInterval = useCallback((): void => {
    if (gameCheckIntervalRef.current) {
      clearInterval(gameCheckIntervalRef.current)
      gameCheckIntervalRef.current = null
    }
  }, [])

  const clearLiveInterval = useCallback((): void => {
    if (liveGameIntervalRef.current) {
      clearInterval(liveGameIntervalRef.current)
      liveGameIntervalRef.current = null
    }
  }, [])

  const clearIntervals = useCallback((): void => {
    clearGameCheckInterval()
    clearLiveInterval()
  }, [clearGameCheckInterval, clearLiveInterval])

  const handleError = useCallback(
    (error: unknown, context: string, gameId?: string): void => {
      const normalizedError = normalizeError(error)
      console.error(`Error in ${context}:`, normalizedError)

      logEvent('live_game_error', {
        context,
        error_message: normalizedError.message,
        game_id: gameId || 'unknown',
      })
    },
    []
  )

  // --- State Updaters ---

  const updateLiveEventData = useCallback(
    (eventInfo: EventGQL) => {
      if (!isMountedRef.current) return

      if (!eventInfo) {
        setLiveGameEvent(null)
        setIsEventLoading(false)
        return
      }

      try {
        setLiveGameEvent(eventInfo)
        updateEventInState(eventInfo)
        setIsEventLoading(false)
      } catch (err) {
        handleError(err, 'updateLiveEventData', eventInfo.slugname)
        setIsEventLoading(false)
      }
    },
    [updateEventInState, handleError]
  )

  // --- Fetching Logic ---

  const fetchEvent = useCallback(
    async (gameId: string): Promise<EventGQL | null> => {
      if (!isMountedRef.current) return null
      const now = Date.now()
      const timeSinceLastFetch = now - lastFetchTimeRef.current
      if (timeSinceLastFetch < DEDUPE_CONSTANT && inFlightRequestRef.current) {
        return inFlightRequestRef.current
      }

      lastFetchTimeRef.current = now
      const eventRequest = executeWithRetry(
        () => fetchEventOperation(gameId),
        `fetchEvent_${gameId}`
      )
      inFlightRequestRef.current = eventRequest

      try {
        const eventInfo = await eventRequest
        if (inFlightRequestRef.current === eventRequest) {
          inFlightRequestRef.current = null
        }

        if (eventInfo && isMountedRef.current) {
          updateLiveEventData(eventInfo)
        }
        return eventInfo
      } catch (err) {
        if (inFlightRequestRef.current === eventRequest) {
          inFlightRequestRef.current = null
        }

        handleError(err, 'fetchEvent', gameId)
        if (isMountedRef.current) {
          setIsEventLoading(false)
        }
        return null
      }
    },
    [updateLiveEventData, handleError]
  )

  const fetchEventOperation = async (gameId: string): Promise<EventGQL> => {
    const eventInfo = await fetchSingleEventInfo(gameId)
    if (!eventInfo || !isMountedRef.current) {
      throw new Error('Event info not found or component unmounted')
    }
    return eventInfo
  }

  const startLiveGameInterval = useCallback(
    async (liveEv: EventGQL) => {
      if (
        liveGameIntervalRef.current !== null &&
        currentGameIdRef.current === liveEv.slugname
      ) {
        return
      }

      if (!liveEv?.slugname || !isMountedRef.current) return

      liveGameIntervalRef.current = setInterval(async () => {
        if (!isMountedRef.current) {
          if (liveGameIntervalRef.current) {
            clearLiveInterval()
          }
          return
        }

        try {
          const eventData = await fetchEvent(liveEv.slugname)
          if (eventData && isMountedRef.current) {
            if (!isLiveGame(eventData)) {
              try {
                updateEventInState(eventData)
                setLiveGameEvent(null)
                currentGameIdRef.current = null
                if (liveGameIntervalRef.current !== null) {
                  clearLiveInterval()
                }
              } catch (updateError) {
                handleError(
                  updateError,
                  'startLiveGameInterval_stateUpdate',
                  liveEv.slugname
                )
              }
            }
          }
        } catch (error) {
          handleError(error, 'startLiveGameInterval', liveEv.slugname)
        }
      }, GAME_POLLING_INTERVAL_TIME)
    },
    [fetchEvent, updateEventInState, clearLiveInterval, handleError]
  )

  const runLiveGameCheck = useCallback(
    (currentEvents: EventGQL[]): void => {
      if (!currentEvents?.length) return

      if (!isMountedRef.current) return

      try {
        const confirmedLiveGame = checkLiveGameRunning(currentEvents)

        if (confirmedLiveGame) {
          const isNewGame = currentGameIdRef.current !== confirmedLiveGame.slugname
          const hasNoInterval = !liveGameIntervalRef.current

          if (isNewGame || hasNoInterval) {
            currentGameIdRef.current = confirmedLiveGame.slugname
            setLiveGameEvent(confirmedLiveGame)
            startLiveGameInterval(confirmedLiveGame)
          }
        } else if (liveGameIntervalRef.current) {
          console.log('🏁 Game ended, clearing polling interval and game ID')
          currentGameIdRef.current = null
          clearInterval(liveGameIntervalRef.current)
          liveGameIntervalRef.current = null
          if (isMountedRef.current) {
            setLiveGameEvent(null)
          }
        } else {
          if (currentGameIdRef.current) {
            currentGameIdRef.current = null
          }
        }
      } catch (err) {
        handleError(err, 'runLiveGameCheck')
      }
    },
    [startLiveGameInterval, handleError]
  )

  useEffect(() => {
    isMountedRef.current = true
    lastFetchTimeRef.current = 0
    inFlightRequestRef.current = null
    setIsEventLoading(false)

    if (!currentGameIdRef.current) {
      setLiveGameEvent(null)
    }

    if (eventsLoading || !events || events.length === 0) return

    runLiveGameCheck(events)

    return () => {
      isMountedRef.current = false
      clearIntervals()
      lastFetchTimeRef.current = 0
      inFlightRequestRef.current = null
    }
  }, [runLiveGameCheck, clearIntervals])

  useEffect(() => {
    if (eventsLoading || !events || events.length === 0) return
    if (gameCheckIntervalRef.current) {
      clearInterval(gameCheckIntervalRef.current)
      gameCheckIntervalRef.current = null
    }

    gameCheckIntervalRef.current = setInterval(() => {
      console.log('🕐 Periodic game check running...')
      if (isMountedRef.current && events) {
        runLiveGameCheck(events)
      } else {
        if (gameCheckIntervalRef.current) {
          clearGameCheckInterval()
        }
      }
    }, GAME_CHECK_INTERVAL_TIME)

    return () => {
      if (gameCheckIntervalRef.current) {
        clearGameCheckInterval()
      }
    }
  }, [runLiveGameCheck, clearGameCheckInterval])

  useEffect(() => {
    if (eventsLoading || !events || events.length === 0) return
    runLiveGameCheck(events)
  }, [events, eventsLoading, runLiveGameCheck])

  return useMemo(
    () => ({
      liveGameEvent,
      isEventLoading,
    }),
    [liveGameEvent, isEventLoading]
  )
}
